import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Drama } from '@/types/drama';

interface Slide {
  drama: Drama;
  slogan: string;
}

interface HeroCarouselProps {
  slides: Slide[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);

  const go = useCallback(
    (next: number) => setActive((next + slides.length) % slides.length),
    [slides.length],
  );

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = setInterval(() => setActive((v) => (v + 1) % slides.length), 5500);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const current = slides[active];

  return (
    <section className="relative w-full h-[460px] md:h-[620px] overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={slide.drama.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === active ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={slide.drama.backdrop}
            alt={`${slide.drama.title}剧照`}
            title={`${slide.drama.title} - 香草视频 高清电视剧在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />

      <div className="relative z-10 w-full h-full px-4 md:px-8">
        <div className="h-full max-w-3xl flex flex-col justify-center text-background-50">
          <span className="inline-flex items-center gap-1.5 w-fit px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-medium mb-4">
            <i className="ri-fire-line" />
            热播推荐
          </span>
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl leading-tight drop-shadow-sm">
            {current.drama.title}
          </h2>
          <p className="mt-4 text-sm md:text-lg text-background-100/90 max-w-xl">{current.slogan}</p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs md:text-sm text-background-100/85">
            {current.drama.types.map((type) => (
              <span key={type} className="px-2 py-0.5 rounded-md bg-background-50/15 backdrop-blur-sm">
                {type}
              </span>
            ))}
            <span>{current.drama.region}</span>
            <span className="text-background-100/50">·</span>
            <span>{current.drama.year}</span>
            <span className="text-background-100/50">·</span>
            <span>{current.drama.actors.slice(0, 2).join(' / ')}</span>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${current.drama.id}`)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/category/${encodeURIComponent(current.drama.types[0])}`)}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apps-2-line" />
              更多{dramaLabel(current.drama)}
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(active - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur-sm text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(active + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur-sm text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.drama.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function dramaLabel(drama: Drama) {
  return drama.types[0] || '同类';
}